import { Edge, MarkerType } from "@xyflow/react"
import {
  WorkflowNode,
  WorkflowEdge,
  ApiWorkflowNode,
  ApiWorkflowCheck,
  TriggerNodeData,
  ConditionNodeData,
  ActionNodeData
} from "lib/types/workflow"
import { OrderWorkflowNodeConditionCheckType as CheckType } from "lib/types/generated/graphql"
import { v4 as uuidv4 } from "uuid"

/** Max `values` allowed on a single ADDRESSES.billingAddress.email check. */
const EMAIL_BATCH_SIZE = 10

/** UI-only check type representing a bulk billingAddress email match.
 *  On save, it expands into a right-leaning OR tree of ADDRESSES leaves. */
export const BILLING_ADDRESS_EMAIL = "BILLING_ADDRESS_EMAIL"

/** UI-only check types for sub-field checks — the API type is broader than what the UI expresses. */
export const PAYMENT_PROVIDER_NAME = "PAYMENT_PROVIDER_NAME"
export const ORDER_LINE_PRODUCT_PARENT_ID = "ORDER_LINE_PRODUCT_PARENT_ID"
export const ORDER_LINE_PRODUCT_VARIANT_ID = "ORDER_LINE_PRODUCT_VARIANT_ID"

const makeBillingEmailLeaf = (emails: string[]): ApiWorkflowCheck => ({
  type: "ADDRESSES",
  fields: {
    billingAddress: {
      email: {
        comparison: "EQUALS",
        values: emails
      }
    }
  }
})

/**
 * Build a right-leaning OR tree of ADDRESSES leaves from a flat email list.
 *
 * For N ≤ 10 → a single ADDRESSES leaf.
 * For 10 < N ≤ 20 → OR(leaf1, leaf2).
 * For N > 20 → OR(leaf1, OR(leaf2, OR(leaf3, ...))) — every additional batch
 * nests one more OR on the right side.
 */
export const buildBillingEmailCheck = (
  emails: string[]
): ApiWorkflowCheck | null => {
  const cleaned = Array.from(
    new Set(emails.map((e) => e.trim()).filter((e) => e.length > 0))
  )
  if (cleaned.length === 0) return null

  const batches: string[][] = []
  for (let i = 0; i < cleaned.length; i += EMAIL_BATCH_SIZE) {
    batches.push(cleaned.slice(i, i + EMAIL_BATCH_SIZE))
  }

  if (batches.length === 1) return makeBillingEmailLeaf(batches[0])

  // Build right-to-left: right = last leaf; fold remaining leaves from the
  // right so each wraps the running "right" inside an OR.
  let right: ApiWorkflowCheck = makeBillingEmailLeaf(batches[batches.length - 1])
  for (let i = batches.length - 2; i >= 0; i--) {
    right = {
      type: "OR",
      check1: makeBillingEmailLeaf(batches[i]),
      check2: right
    }
  }
  return right
}

/**
 * Try to recognise the inverse of {@link buildBillingEmailCheck}: a check
 * that is either a single ADDRESSES leaf (billing email only), or a
 * right-leaning OR tree made up of such leaves.
 *
 * Returns the combined, deduped email list if the pattern matches, otherwise
 * `null` (so the loader falls back to the generic check rendering).
 */
export const extractBillingEmailCheck = (
  check: ApiWorkflowCheck | undefined
): string[] | null => {
  if (!check) return null

  if (check.type === "ADDRESSES") {
    const fields = check.fields
    if (!fields?.billingAddress) return null
    if (fields.shippingAddress) return null
    const addr = fields.billingAddress
    const keys = Object.keys(addr).filter((k) => addr[k as keyof typeof addr] !== undefined)
    if (keys.length !== 1 || keys[0] !== "email") return null
    const emailCheck = addr.email
    if (!emailCheck || emailCheck.comparison !== "EQUALS") return null
    return [...emailCheck.values]
  }

  if (check.type === "OR") {
    const left = extractBillingEmailCheck(check.check1)
    const right = extractBillingEmailCheck(check.check2)
    if (left && right) return [...left, ...right]
    return null
  }

  return null
}

/**
 * Recognise a "field-based" leaf check — PAYMENT_PROVIDER with a `name`
 * filter, or ORDER_LINE with a `productParentId` / `productVariantId` filter.
 *
 * Returns the UI-side shape `{ checkType, comparison, values }` so the
 * condition form can render, or `null` if the payload doesn't match any
 * known variant (caller falls back to the generic rendering).
 */
export const extractFieldCheck = (
  check: ApiWorkflowCheck | undefined
): { checkType: string; comparison: string; values: string[] } | null => {
  if (!check?.fields) return null

  if (check.type === CheckType.PaymentProvider && check.fields.name) {
    return {
      checkType: PAYMENT_PROVIDER_NAME,
      comparison: check.fields.name.comparison,
      values: [...check.fields.name.values]
    }
  }

  if (check.type === CheckType.OrderLine) {
    // Prefer variantId over parentId when both are present — UI can only
    // express one at a time, and variant is the more specific match.
    if (check.fields.productVariantId) {
      return {
        checkType: ORDER_LINE_PRODUCT_VARIANT_ID,
        comparison: check.fields.productVariantId.comparison,
        values: [...check.fields.productVariantId.values]
      }
    }
    if (check.fields.productParentId) {
      return {
        checkType: ORDER_LINE_PRODUCT_PARENT_ID,
        comparison: check.fields.productParentId.comparison,
        values: [...check.fields.productParentId.values]
      }
    }
  }

  return null
}

const NODE_X_SPACING = 300
const NODE_Y_SPACING = 180

const makeEdge = (
  source: string,
  sourceHandle: string,
  target: string
): WorkflowEdge => ({
  id: `e-${source}-${target}`,
  source,
  sourceHandle,
  target,
  targetHandle: "input",
  markerEnd: { type: MarkerType.ArrowClosed },
  animated: true
})

// ────────────────────────────────────────────────────────────
// Initial state: just a trigger node
// ────────────────────────────────────────────────────────────

export const getInitialNodes = (): {
  nodes: WorkflowNode[]
  edges: WorkflowEdge[]
} => {
  const triggerNode: WorkflowNode = {
    id: "trigger-1",
    type: "trigger",
    position: { x: 250, y: 0 },
    data: {
      nodeType: "TRIGGER",
      label: "Order Created",
      triggerType: "ORDER_CREATED"
    }
  }

  return {
    nodes: [triggerNode],
    edges: []
  }
}

// ────────────────────────────────────────────────────────────
// Flow → API: serialize canvas nodes to recursive API format
// ────────────────────────────────────────────────────────────

const buildApiNode = (
  nodeId: string,
  nodes: WorkflowNode[],
  edges: Edge[],
  visited: Set<string> = new Set()
): ApiWorkflowNode | null => {
  if (visited.has(nodeId)) return null
  visited.add(nodeId)

  const node = nodes.find((n) => n.id === nodeId)
  if (!node) return null

  if (node.data.nodeType === "ACTION") {
    const actionData = node.data as ActionNodeData
    const doPayload: ApiWorkflowNode["do"] = { type: actionData.actionType }
    if (actionData.actionType === "CANCELLATION" && actionData.reason) {
      doPayload.reason = {
        code: actionData.reason,
        description: actionData.reasonDescription ?? actionData.reason
      }
    }
    if (actionData.actionType === "CUSTOM_STATE" && actionData.customState) {
      doPayload.customState = actionData.customState
    }
    if (actionData.actionType === "DELIVERY") {
      doPayload.selection = {
        giftCardProducts: actionData.deliveryGiftCardProducts ?? false
      }
    }

    const result: ApiWorkflowNode = {
      type: "ACTION",
      name: actionData.label,
      do: doPayload,
      then: []
    }

    const childEdge = edges.find((e) => e.source === nodeId)
    if (childEdge) {
      const childNode = buildApiNode(childEdge.target, nodes, edges, visited)
      if (childNode) {
        result.then = [childNode]
      }
    }

    return result
  }

  if (node.data.nodeType === "CONDITION") {
    const conditionData = node.data as ConditionNodeData

    let checkPayload: ApiWorkflowCheck

    if (conditionData.checkType === BILLING_ADDRESS_EMAIL) {
      const expanded = buildBillingEmailCheck(conditionData.values ?? [])
      // Fall back to an empty ADDRESSES leaf so the node still serialises.
      checkPayload = expanded ?? makeBillingEmailLeaf([])
    } else if (conditionData.checkType === CheckType.CustomHttp) {
      checkPayload = { type: CheckType.CustomHttp }
      checkPayload.endpoint = conditionData.endpoint
      if (conditionData.customData) {
        try {
          checkPayload.customData = JSON.parse(conditionData.customData)
        } catch {
          // Invalid JSON — skip customData
        }
      }
    } else if (conditionData.checkType === PAYMENT_PROVIDER_NAME) {
      checkPayload = {
        type: CheckType.PaymentProvider,
        fields: {
          name: {
            comparison: conditionData.comparison,
            values: conditionData.values ?? []
          }
        }
      }
    } else if (conditionData.checkType === ORDER_LINE_PRODUCT_PARENT_ID) {
      checkPayload = {
        type: CheckType.OrderLine,
        fields: {
          productParentId: {
            comparison: conditionData.comparison,
            values: conditionData.values ?? []
          }
        }
      }
    } else if (conditionData.checkType === ORDER_LINE_PRODUCT_VARIANT_ID) {
      checkPayload = {
        type: CheckType.OrderLine,
        fields: {
          productVariantId: {
            comparison: conditionData.comparison,
            values: conditionData.values ?? []
          }
        }
      }
    } else {
      // Unknown UI check type — emit verbatim so the backend surfaces the
      // mismatch rather than us silently swallowing bad data.
      checkPayload = {
        type: conditionData.checkType,
        comparison: conditionData.comparison,
        values: conditionData.values
      }
    }

    const result: ApiWorkflowNode = {
      type: "CONDITION",
      name: conditionData.label,
      check: checkPayload,
      then: [],
      otherwise: []
    }

    const trueEdge = edges.find(
      (e) => e.source === nodeId && e.sourceHandle === "true-output"
    )
    const falseEdge = edges.find(
      (e) => e.source === nodeId && e.sourceHandle === "false-output"
    )

    if (trueEdge) {
      const trueChild = buildApiNode(trueEdge.target, nodes, edges, visited)
      if (trueChild) {
        result.then = [trueChild]
      }
    }
    if (falseEdge) {
      const falseChild = buildApiNode(falseEdge.target, nodes, edges, visited)
      if (falseChild) {
        result.otherwise = [falseChild]
      }
    }

    return result
  }

  return null
}

export const flowToApiJson = (
  nodes: WorkflowNode[],
  edges: Edge[]
): ApiWorkflowNode[] => {
  const triggerNode = nodes.find(
    (n) => (n.data as TriggerNodeData).nodeType === "TRIGGER"
  )
  if (!triggerNode) return []

  const triggerEdges = edges.filter((e) => e.source === triggerNode.id)
  const result: ApiWorkflowNode[] = []

  for (const edge of triggerEdges) {
    const apiNode = buildApiNode(edge.target, nodes, edges)
    if (apiNode) result.push(apiNode)
  }

  return result
}

/**
 * Validate the serialized workflow before sending it to the backend. Returns
 * a flat list of human-readable errors; an empty list means OK. Backend
 * validator requires every CONDITION node to have a Yes branch ("then" must
 * contain at least 1 item) so we mirror that here for faster feedback.
 */
export const validateApiWorkflow = (nodes: ApiWorkflowNode[]): string[] => {
  const errors: string[] = []

  const walk = (ns: ApiWorkflowNode[]) => {
    for (const n of ns) {
      if (n.type === "CONDITION") {
        if (!n.then || n.then.length === 0) {
          errors.push(
            `Condition "${n.name}" is missing a Yes branch — connect a node to the Yes handle.`
          )
        } else {
          walk(n.then)
        }
        if (n.otherwise && n.otherwise.length > 0) walk(n.otherwise)
      }
    }
  }

  walk(nodes)
  return errors
}

// ────────────────────────────────────────────────────────────
// API → Flow: deserialize API nodes into canvas nodes
// ────────────────────────────────────────────────────────────

const processApiNode = (
  apiNode: ApiWorkflowNode,
  parentId: string,
  sourceHandle: string,
  x: number,
  y: number,
  nodes: WorkflowNode[],
  edges: WorkflowEdge[]
): void => {
  const nodeId = uuidv4()

  edges.push(makeEdge(parentId, sourceHandle, nodeId))

  if (apiNode.type === "ACTION") {
    const node: WorkflowNode = {
      id: nodeId,
      type: "action",
      position: { x, y },
      data: {
        nodeType: "ACTION",
        label: apiNode.name,
        actionType: apiNode.do?.type ?? "CANCELLATION",
        reason:
          apiNode.do?.type === "CANCELLATION"
            ? apiNode.do?.reason?.code
            : undefined,
        reasonDescription:
          apiNode.do?.type === "CANCELLATION"
            ? apiNode.do?.reason?.description
            : undefined,
        customState:
          apiNode.do?.type === "CUSTOM_STATE"
            ? apiNode.do?.customState
            : undefined,
        deliveryGiftCardProducts:
          apiNode.do?.type === "DELIVERY"
            ? apiNode.do?.selection?.giftCardProducts
            : undefined
      }
    }
    nodes.push(node)

    if (apiNode.then) {
      for (const child of apiNode.then) {
        processApiNode(child, nodeId, "output", x, y + NODE_Y_SPACING, nodes, edges)
      }
    }
  } else if (apiNode.type === "CONDITION") {
    const isCustomHttp = apiNode.check?.type === "CUSTOM_HTTP"

    // Detect a billing-email-match tree (ADDRESSES leaf or OR-chain of them)
    // and collapse back to the single UI node that produced it.
    const billingEmails = extractBillingEmailCheck(apiNode.check)

    // Detect field-based shapes (PAYMENT_PROVIDER with `name` filter,
    // ORDER_LINE with productParentId/productVariantId) and collapse back
    // to the UI-level checkType the condition form uses.
    const fieldCheck = billingEmails ? null : extractFieldCheck(apiNode.check)

    let data: ConditionNodeData
    if (billingEmails) {
      data = {
        nodeType: "CONDITION",
        label: apiNode.name,
        checkType: BILLING_ADDRESS_EMAIL,
        comparison: "EQUALS",
        values: billingEmails
      }
    } else if (fieldCheck) {
      data = {
        nodeType: "CONDITION",
        label: apiNode.name,
        checkType: fieldCheck.checkType,
        comparison: fieldCheck.comparison,
        values: fieldCheck.values
      }
    } else {
      data = {
        nodeType: "CONDITION",
        label: apiNode.name,
        checkType: apiNode.check?.type ?? "",
        comparison: isCustomHttp ? "" : (apiNode.check?.comparison ?? "EQUALS"),
        values: isCustomHttp ? [] : (apiNode.check?.values ?? []).map(String),
        endpoint: isCustomHttp ? apiNode.check?.endpoint : undefined,
        customData:
          isCustomHttp && apiNode.check?.customData
            ? JSON.stringify(apiNode.check.customData, null, 2)
            : undefined
      }
    }

    const node: WorkflowNode = {
      id: nodeId,
      type: "condition",
      position: { x, y },
      data
    }
    nodes.push(node)

    if (apiNode.then) {
      for (const child of apiNode.then) {
        processApiNode(
          child,
          nodeId,
          "true-output",
          x - NODE_X_SPACING,
          y + NODE_Y_SPACING,
          nodes,
          edges
        )
      }
    }
    if (apiNode.otherwise) {
      for (const child of apiNode.otherwise) {
        processApiNode(
          child,
          nodeId,
          "false-output",
          x + NODE_X_SPACING,
          y + NODE_Y_SPACING,
          nodes,
          edges
        )
      }
    }
  }
}

export const apiJsonToFlow = (
  apiNodes: ApiWorkflowNode[],
  triggerType: string = "ORDER_CREATED"
): { nodes: WorkflowNode[]; edges: WorkflowEdge[] } => {
  const triggerId = "trigger-1"
  const triggerNode: WorkflowNode = {
    id: triggerId,
    type: "trigger",
    position: { x: 250, y: 0 },
    data: {
      nodeType: "TRIGGER",
      label: triggerType === "ORDER_CREATED" ? "Order Created" : triggerType,
      triggerType
    }
  }

  const nodes: WorkflowNode[] = [triggerNode]
  const edges: WorkflowEdge[] = []

  for (let i = 0; i < apiNodes.length; i++) {
    const xOffset = i * NODE_X_SPACING
    processApiNode(apiNodes[i], triggerId, "output", 250 + xOffset, NODE_Y_SPACING, nodes, edges)
  }

  return { nodes, edges }
}
